
import React, { useState, useEffect, useCallback } from 'react';
import { db } from './services/db';
import { User } from './types';
import Dashboard from './pages/Dashboard';
import Clients from './pages/Clients';
import ClientDetail from './pages/ClientDetail';
import Timesheet from './pages/Timesheet';
import Settings from './pages/Settings';
import Login from './pages/Login';
import Sidebar from './components/Sidebar';
import BottomNav from './components/BottomNav';
import { InstallPrompt } from './components/InstallPrompt';
import { AlertCircle, X } from 'lucide-react';

type View = 'dashboard' | 'clients' | 'client-detail' | 'timesheet' | 'settings';

export default function App() {
  const [user, setUser] = useState<User | null>(null);
  const [currentView, setCurrentView] = useState<View>('dashboard');
  const [selectedClientId, setSelectedClientId] = useState<string | null>(null);
  const [selectedPeriodId, setSelectedPeriodId] = useState<string | null>(null);
  const [isInitializing, setIsInitializing] = useState(true);
  const [isDirty, setIsDirty] = useState(false);
  
  // Navigation Guard State
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [pendingAction, setPendingAction] = useState<(() => void) | null>(null);

  useEffect(() => {
    const loggedUser = db.getCurrentUser();
    if (loggedUser) {
      setUser(loggedUser);
      if (loggedUser.largeTextMode) {
        document.body.classList.add('large-text-mode');
      }
    }
    setIsInitializing(false);

    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (isDirty) {
        e.preventDefault();
        e.returnValue = '';
      }
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [isDirty]);

  const safeAction = useCallback((action: () => void, force: boolean = false) => {
    if (isDirty && !force) {
      setPendingAction(() => action);
      setShowConfirmModal(true);
    } else {
      setIsDirty(false); // Explicitly clear dirty on navigation
      action();
    }
  }, [isDirty]);

  const setView = (view: View) => {
    safeAction(() => {
      setCurrentView(view);
    });
  };

  const handleLogin = (email: string, name?: string, rate?: number) => {
    const u = db.login(email, name, rate);
    setUser(u);
    setCurrentView('dashboard');
  };

  const handleLogout = () => {
    safeAction(() => {
      db.logout();
      setUser(null);
      setCurrentView('dashboard');
      document.body.classList.remove('large-text-mode');
    });
  };

  const handleUserUpdate = (updatedUser: User) => {
    db.updateUser(updatedUser);
    setUser(updatedUser);
    if (updatedUser.largeTextMode) {
      document.body.classList.add('large-text-mode');
    } else {
      document.body.classList.remove('large-text-mode');
    }
  };

  const handleDeleteAccount = () => {
    db.deleteAccount();
    setUser(null);
    setCurrentView('dashboard');
    document.body.classList.remove('large-text-mode');
  };

  const navigateToClient = (id: string) => {
    safeAction(() => {
      if (id === 'new') {
        setCurrentView('clients');
        return;
      }
      setSelectedClientId(id);
      setCurrentView('client-detail');
    });
  };

  const navigateToTimesheet = (clientId: string, periodId?: string) => {
    safeAction(() => {
      setSelectedClientId(clientId);
      setSelectedPeriodId(periodId || null);
      setCurrentView('timesheet');
    });
  };

  const handleConfirmLeave = () => {
    if (pendingAction) {
      pendingAction();
      setPendingAction(null);
    }
    setShowConfirmModal(false);
    setIsDirty(false);
  };

  if (isInitializing) {
    return <div className="min-h-screen bg-slate-50 flex items-center justify-center">
      <div className="animate-pulse flex flex-col items-center gap-4">
        <div className="w-12 h-12 bg-blue-600 rounded-2xl"></div>
      </div>
    </div>;
  }

  if (!user) return <Login onLogin={handleLogin} />;

  return (
    <div className="flex flex-col md:flex-row min-h-screen bg-slate-50">
      <div className="no-print hidden md:flex">
        <Sidebar currentView={currentView} setView={setView} onLogout={handleLogout} />
      </div>
      
      {/* 
          CALCULATED PADDING: 
          pb-24 (mobile nav) + safe-area-inset-bottom
          pt-8 (increased default) + safe-area-inset-top for notch clearance
      */}
      <main className="flex-1 overflow-y-auto pb-[calc(env(safe-area-inset-bottom)+7rem)] md:pb-8 p-4 md:p-6 pt-[calc(env(safe-area-inset-top)+2.5rem)] md:pt-6">
        <div className="max-w-6xl mx-auto">
          {currentView === 'dashboard' && (
            <Dashboard user={user} onViewClient={navigateToClient} onAddTime={(clientId) => navigateToTimesheet(clientId)} />
          )}
          {currentView === 'clients' && (
            <Clients user={user} onViewClient={navigateToClient} />
          )}
          {currentView === 'client-detail' && selectedClientId && (
            <ClientDetail user={user} clientId={selectedClientId} onBack={() => setView('clients')} onEnterTime={(periodId) => navigateToTimesheet(selectedClientId, periodId)} />
          )}
          {currentView === 'timesheet' && selectedClientId && (
            <Timesheet 
              user={user} 
              clientId={selectedClientId} 
              periodId={selectedPeriodId} 
              onDirtyChange={setIsDirty} 
              onBack={(force = false) => {
                safeAction(() => {
                  const wasInPeriod = !!selectedPeriodId;
                  setSelectedPeriodId(null);
                  setCurrentView(wasInPeriod ? 'client-detail' : 'dashboard');
                }, force);
              }} 
            />
          )}
          {currentView === 'settings' && (
            <Settings user={user} onLogout={handleLogout} onUserUpdate={handleUserUpdate} onDeleteAccount={handleDeleteAccount} />
          )}
        </div>
      </main>

      {/* Modern Confirmation Modal */}
      {showConfirmModal && (
        <div className="fixed inset-0 z-[200] flex items-center justify-center p-4 bg-slate-900/40 backdrop-blur-sm animate-in fade-in duration-200">
          <div className="bg-white w-full max-sm rounded-[2rem] shadow-2xl p-8 space-y-6 relative overflow-hidden animate-in zoom-in-95 duration-300">
            <div className="absolute top-0 left-0 right-0 h-1.5 bg-blue-600"></div>
            <button 
              onClick={() => setShowConfirmModal(false)} 
              className="absolute top-6 right-6 text-slate-300 hover:text-slate-600 transition-colors"
            >
              <X size={20} />
            </button>
            
            <div className="flex flex-col items-center text-center space-y-4">
              <div className="bg-blue-50 p-4 rounded-3xl text-blue-600">
                <AlertCircle size={32} strokeWidth={2.5} />
              </div>
              <div className="space-y-2">
                <h3 className="text-xl font-black text-slate-900 tracking-tight">Unsaved Changes</h3>
                <p className="text-sm text-slate-500 font-medium leading-relaxed">
                  You have unsaved work on this page. You need to save or you will lose your changes before exiting.
                </p>
              </div>
            </div>

            <div className="flex flex-col gap-2 pt-2">
              <button 
                onClick={() => setShowConfirmModal(false)}
                className="w-full py-4 bg-blue-600 text-white rounded-2xl font-black text-xs uppercase tracking-[0.15em] hover:bg-blue-700 transition shadow-lg shadow-blue-500/20 active:scale-95"
              >
                Stay & Save Work
              </button>
              <button 
                onClick={handleConfirmLeave}
                className="w-full py-4 bg-white text-slate-400 hover:text-red-500 rounded-2xl font-black text-xs uppercase tracking-[0.15em] transition active:scale-95"
              >
                Leave without saving
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="md:hidden no-print">
        <BottomNav currentView={currentView} setView={setView} />
      </div>
      <InstallPrompt />
    </div>
  );
}
